import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';

import classNames from 'classnames';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { ModalRefProps } from 'src/common/components/Modal';
import { OrbitIcons } from 'src/common/constants.';

import {
  cleanupSharedAccountFreeSwitch,
  deleteAccount,
  deleteFreeswitchStack,
  fetchAccounts,
  refreshAccountSipTrunk,
  regenerateSharedAccountFreeSwitch,
  sipTrunkToastMessage,
  InterstellarAccount,
  accountIsGeoreferenced,
} from '../api';
import type { AccountHealth } from '../api';
import {
  AccountList,
  AccountMap,
  CreateAccountModal,
  DeleteAccountModal,
  SipTrunkSummary,
} from '../components';
import type { DeleteAccountModalRef } from '../components';

type DashboardView = 'cuentas' | 'trunks';
type AccountsPane = 'lista' | 'mapa';

const ShortcutRow: React.FC<{ keys: string; desc: string }> = ({ keys, desc }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-gray-600">{desc}</span>
    <div className="flex gap-1">
      {keys.split(' / ').map((k) => (
        <kbd
          key={k}
          className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-gray-700"
        >
          {k}
        </kbd>
      ))}
    </div>
  </div>
);

export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [view] = useState<DashboardView>('cuentas');
  const [accountsPane, setAccountsPane] = useState<AccountsPane>('lista');
  const [highlightedAccountId, setHighlightedAccountId] = useState<number | null>(null);
  const createAccountModalRef = useRef<ModalRefProps>(null);
  const deleteAccountModalRef = useRef<DeleteAccountModalRef>(null);

  const queryCache = useQueryCache();

  const {
    data: accounts = [],
    isLoading,
    refetch,
  } = useQuery<InterstellarAccount[], Error>('interstellar-accounts', fetchAccounts, {
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const filteredAccounts = useMemo(
    () =>
      accounts.filter((account) => {
        const q = searchTerm.toLowerCase();
        return (
          account.name.toLowerCase().includes(q) ||
          account.phone_number?.toLowerCase().includes(q) ||
          account.call_rescue_number?.toLowerCase().includes(q) ||
          account.search_index_name?.toLowerCase().includes(q) ||
          account.city?.toLowerCase().includes(q) ||
          account.state?.toLowerCase().includes(q) ||
          account.sip_trunk?.toLowerCase().includes(q)
        );
      }),
    [accounts, searchTerm]
  );

  const accountsOnMap = useMemo(
    () => filteredAccounts.filter(accountIsGeoreferenced),
    [filteredAccounts]
  );

  // Health polling removed - was causing excessive API calls to /freeswitch/overview
  const healthMap: Record<number, AccountHealth> = {};

  const georefCount = useMemo(() => accounts.filter(accountIsGeoreferenced).length, [accounts]);
  const notGeorefCount = accounts.length - georefCount;

  // Navigate to account detail page
  const handleAccountSelect = useCallback(
    (account: InterstellarAccount): void => {
      navigate(`/app/phone_settings/accounts/${account.id}`);
    },
    [navigate]
  );

  const handleTableAccountSelect = useCallback(
    (account: InterstellarAccount): void => {
      navigate(`/app/phone_settings/accounts/${account.id}`);
    },
    [navigate]
  );

  const toastMutationError = (err: unknown, fallback: string): void => {
    const data = (err as { response?: { data?: { detail?: string; message?: string } } })?.response
      ?.data;
    Toast.error(data?.detail || data?.message || fallback);
  };

  // The trunk lives in the instance itself, so the column can only be filled by
  // asking the account's FreeSWITCH for its gateways, dialplan and proxy.
  const [refreshingSipTrunkId, setRefreshingSipTrunkId] = useState<number | null>(null);
  const [refreshSipTrunk] = useMutation((accountId: number) => refreshAccountSipTrunk(accountId), {
    onSuccess: async (result) => {
      await refetch();
      Toast.success(sipTrunkToastMessage(result));
    },
    onError: (err: unknown) =>
      toastMutationError(err, 'No se pudo leer el SIP trunk desde FreeSWITCH.'),
    onSettled: (_result, _err, accountId) =>
      setRefreshingSipTrunkId((prev) => (prev === accountId ? null : prev)),
  });

  const handleRefreshSipTrunk = useCallback(
    (account: InterstellarAccount): void => {
      setRefreshingSipTrunkId(account.id);
      refreshSipTrunk(account.id);
    },
    [refreshSipTrunk]
  );

  // Delete account mutation
  const [deletingAccountId, setDeletingAccountId] = useState<number | null>(null);
  const [deleteAccountMutation] = useMutation((accountId: number) => deleteAccount(accountId), {
    onSuccess: async () => {
      await refetch();
      deleteAccountModalRef.current?.hide();
      Toast.success(`Cuenta eliminada de Interstellar`);
    },
    onError: (err: unknown) =>
      toastMutationError(
        err,
        'No se pudo eliminar la cuenta. Revisa DELETE /account/ en Interstellar.'
      ),
    onSettled: () => setDeletingAccountId(null),
  });

  const handleDeleteAccount = useCallback((account: InterstellarAccount): void => {
    deleteAccountModalRef.current?.show(account);
  }, []);

  const handleConfirmDelete = useCallback(
    async (account: InterstellarAccount, deleteInfra: boolean): Promise<void> => {
      setDeletingAccountId(account.id);
      try {
        // For shared FreeSWITCH accounts, clean up FreeSWITCH resources first
        if (account.freeswitch_group_id) {
          const cleanupResult = await cleanupSharedAccountFreeSwitch(account.id);
          Toast.success(
            `FreeSWITCH limpiado: ${cleanupResult.removed.users} usuarios, ` +
              `${cleanupResult.removed.dialplan_files.length} archivos de dialplan`
          );
        } else if (deleteInfra) {
          // For dedicated FreeSWITCH instances, delete the entire infrastructure
          const result = await deleteFreeswitchStack(account.id);
          Toast.success(
            `Eliminando instancia ${result.instance_id || 'N/A'}. ` +
              `Verifica en AWS Console: ${result.status}`
          );
        }
        deleteAccountMutation(account.id);
      } catch (err) {
        toastMutationError(err, 'No se pudo eliminar la infraestructura de FreeSWITCH');
        setDeletingAccountId(null);
      }
    },
    [deleteAccountMutation]
  );

  // Regenerate FreeSWITCH config mutation (for shared accounts only)
  const [regeneratingFreeSwitchId, setRegeneratingFreeSwitchId] = useState<number | null>(null);
  const [regenerateFreeSwitch] = useMutation(
    (accountId: number) => regenerateSharedAccountFreeSwitch(accountId),
    {
      onSuccess: async (result) => {
        await refetch();
        const { regenerated } = result;
        Toast.success(
          `FreeSWITCH regenerado: ${regenerated.user_xmls} usuarios, ` +
            `domain=${regenerated.domain_xml}, routing=${regenerated.shared_outbound_routing}`
        );
      },
      onError: (err: unknown) =>
        toastMutationError(err, 'No se pudo regenerar la configuración de FreeSWITCH'),
      onSettled: (_result, _err, accountId) =>
        setRegeneratingFreeSwitchId((prev) => (prev === accountId ? null : prev)),
    }
  );

  const handleRegenerateFreeSwitch = useCallback(
    (account: InterstellarAccount): void => {
      if (!account.freeswitch_group_id) {
        Toast.error('Solo se puede regenerar cuentas en FreeSWITCH compartido');
        return;
      }
      setRegeneratingFreeSwitchId(account.id);
      regenerateFreeSwitch(account.id);
    },
    [regenerateFreeSwitch]
  );

  const searchInputRef = useRef<HTMLInputElement>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';

      if (e.key === 'Escape') {
        if (isInput) {
          (target as HTMLInputElement).blur();
        }
        return;
      }

      if (isInput || target.isContentEditable) return;

      // Don't hijack keys pressed on interactive elements (buttons, links,
      // dialogs): Enter there must click the focused control, not navigate.
      if (target.closest('button, a, select, [role="dialog"]')) return;

      if (e.key === '/') {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }

      if (e.key === '?' && e.shiftKey) {
        e.preventDefault();
        setShowShortcuts((v) => !v);
        return;
      }

      // Navigate through accounts with arrow keys
      if ((e.key === 'ArrowDown' || e.key === 'j') && filteredAccounts.length > 0) {
        e.preventDefault();
        const currentIdx = filteredAccounts.findIndex((a) => a.id === highlightedAccountId);
        const nextIdx = currentIdx < filteredAccounts.length - 1 ? currentIdx + 1 : 0;
        setHighlightedAccountId(filteredAccounts[nextIdx].id);
        return;
      }

      if ((e.key === 'ArrowUp' || e.key === 'k') && filteredAccounts.length > 0) {
        e.preventDefault();
        const currentIdx = filteredAccounts.findIndex((a) => a.id === highlightedAccountId);
        const prevIdx = currentIdx > 0 ? currentIdx - 1 : filteredAccounts.length - 1;
        setHighlightedAccountId(filteredAccounts[prevIdx].id);
        return;
      }

      // Enter to open the highlighted account
      if (e.key === 'Enter' && highlightedAccountId != null) {
        e.preventDefault();
        navigate(`/app/phone_settings/accounts/${highlightedAccountId}`);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredAccounts, highlightedAccountId, navigate]);

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-white-2.0 bg-primary-2.0 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-lg font-bold text-white-0.0">Tablero de control</h1>
              {isLoading ? (
                <p className="text-xs text-white-1.0">Loading accounts...</p>
              ) : (
                <p className="text-xs text-white-1.0">
                  {filteredAccounts.length} mostradas · {georefCount} en mapa
                  {notGeorefCount > 0 ? ` · ${notGeorefCount} sin georreferencia` : ''}
                </p>
              )}
            </div>
            {!isLoading && accounts.length === 0 && (
              <button
                type="button"
                onClick={() => refetch()}
                className="flex items-center gap-1 rounded-md bg-white/10 px-3 py-1 text-xs text-white-0.0 hover:bg-white/20"
              >
                <Icon name={OrbitIcons.RefreshCw} size={12} />
                Retry
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/app/phone_settings/accounts/create')}
              className="btn btn-secondary flex items-center gap-2"
              title="Crear nueva cuenta"
            >
              <Icon name={OrbitIcons.PlusCircle} size={16} />
              Nueva Cuenta
            </button>
            <label className="group flex w-64 cursor-text items-center gap-3 rounded-full border border-white/25 bg-white-0.0 py-2.5 pl-4 pr-5 shadow-md transition-shadow focus-within:border-white/80 focus-within:shadow-lg focus-within:ring-2 focus-within:ring-white/35 sm:w-72">
              <Icon
                name={OrbitIcons.Search}
                size={18}
                className="flex-shrink-0 text-gray-3.0 transition-colors group-focus-within:text-primary-2.0"
              />
              <input
                ref={searchInputRef}
                type="search"
                enterKeyHint="search"
                autoComplete="off"
                placeholder="Buscar cuentas… ( / )"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-medium text-black-1.0 placeholder-gray-2.0 focus:outline-none focus:ring-0"
              />
            </label>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {view === 'trunks' ? (
          <SipTrunkSummary
            accounts={filteredAccounts}
            selectedAccountId={highlightedAccountId ?? undefined}
            onAccountSelect={handleTableAccountSelect}
          />
        ) : (
          <div className="relative flex-1 min-h-0">
            <div className="absolute left-3 top-3 z-20 flex items-center rounded-lg bg-white-0.0 p-0.5 shadow-md">
              <button
                type="button"
                onClick={() => setAccountsPane('lista')}
                className={classNames(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  accountsPane === 'lista'
                    ? 'bg-primary-2.0 text-white-0.0'
                    : 'text-gray-2.0 hover:text-primary-2.0'
                )}
              >
                <Icon name={OrbitIcons.List} size={14} />
                Listado
              </button>
              <button
                type="button"
                onClick={() => setAccountsPane('mapa')}
                className={classNames(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  accountsPane === 'mapa'
                    ? 'bg-primary-2.0 text-white-0.0'
                    : 'text-gray-2.0 hover:text-primary-2.0'
                )}
              >
                <Icon name={OrbitIcons.Map} size={14} />
                Mapa
              </button>
            </div>
            {accountsPane === 'lista' ? (
              <div className="pt-14 h-full">
                <AccountList
                  accounts={filteredAccounts}
                  selectedAccountId={highlightedAccountId ?? undefined}
                  onAccountSelect={handleAccountSelect}
                  healthMap={healthMap}
                  isLoading={isLoading}
                  onRefreshSipTrunk={handleRefreshSipTrunk}
                  refreshingSipTrunkId={refreshingSipTrunkId}
                  onDeleteAccount={handleDeleteAccount}
                  deletingAccountId={deletingAccountId}
                  onRegenerateFreeSwitch={handleRegenerateFreeSwitch}
                  regeneratingFreeSwitchId={regeneratingFreeSwitchId}
                />
              </div>
            ) : (
              <AccountMap
                accounts={accountsOnMap}
                selectedAccount={
                  highlightedAccountId
                    ? accountsOnMap.find((a) => a.id === highlightedAccountId) ?? null
                    : null
                }
                onAccountSelect={handleAccountSelect}
                placementMode={false}
                draftLatLng={null}
                healthMap={healthMap}
              />
            )}
          </div>
        )}
      </div>

      {filteredAccounts.length > 0 && accountsPane === 'mapa' && (
        <div className="border-t border-white-2.0 bg-white-0.0">
          <div className="flex items-center gap-2 p-2 overflow-x-auto">
            {filteredAccounts.slice(0, 40).map((account) => {
              const onMap = accountIsGeoreferenced(account);
              const health = healthMap[account.id];
              const healthDotColor = health
                ? {
                    critical: 'bg-red-500',
                    warning: 'bg-amber-400',
                    healthy: 'bg-green-500',
                    inactive: 'bg-gray-400',
                  }[health.level]
                : null;
              return (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => handleAccountSelect(account)}
                  className={classNames(
                    'flex max-w-xs flex-shrink-0 items-center gap-2 rounded-full border px-3 py-1 text-left text-xs font-medium transition-colors',
                    highlightedAccountId === account.id
                      ? 'border-primary-2.0 bg-primary-2.0 text-white-0.0'
                      : 'border-white-2.0 bg-white-1.0 text-gray-2.0 hover:border-primary-2.0 hover:text-primary-2.0'
                  )}
                  title={onMap ? account.name : `${account.name} — sin georreferencia`}
                >
                  {healthDotColor && (
                    <span
                      className={classNames(
                        'inline-block h-2 w-2 flex-shrink-0 rounded-full',
                        healthDotColor
                      )}
                    />
                  )}
                  <span className="truncate">{account.name}</span>
                  {!onMap && (
                    <span
                      className={classNames(
                        'flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                        highlightedAccountId === account.id
                          ? 'bg-white/20 text-white-0.0'
                          : 'bg-amber-100 text-amber-900'
                      )}
                    >
                      Sin mapa
                    </span>
                  )}
                </button>
              );
            })}
            {filteredAccounts.length > 40 && (
              <span className="flex-shrink-0 px-3 py-1 text-xs text-gray-2.0">
                +{filteredAccounts.length - 40} more
              </span>
            )}
          </div>
        </div>
      )}
      <CreateAccountModal
        ref={createAccountModalRef}
        onAccountCreated={() => {
          queryCache.invalidateQueries('interstellar-accounts');
          refetch();
        }}
      />
      <DeleteAccountModal
        ref={deleteAccountModalRef}
        onConfirm={handleConfirmDelete}
        isDeleting={deletingAccountId !== null}
      />

      {/* Shortcuts help button */}
      <button
        type="button"
        onClick={() => setShowShortcuts(true)}
        className="bottom-4 right-4 fixed z-20 flex items-center justify-center w-8 h-8 text-xs font-bold text-white transition-transform bg-gray-800 rounded-full shadow-lg"
        title="Atajos de teclado (Shift + ?)"
      >
        ?
      </button>

      {/* Shortcuts overlay */}
      {showShortcuts && (
        <>
          <div className="bg-black/40 fixed inset-0 z-40" onClick={() => setShowShortcuts(false)} />
          <div className="top-1/2 left-1/2 w-80 rounded-xl fixed z-50 p-6 -translate-x-1/2 -translate-y-1/2 bg-white border border-gray-200 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">Atajos de teclado</h3>
              <button
                type="button"
                onClick={() => setShowShortcuts(false)}
                className="hover:text-gray-600 text-gray-400"
              >
                <Icon name={OrbitIcons.X} size={16} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <p className="mb-1.5 text-[10px] font-bold tracking-wide text-gray-400 uppercase">
                  Navegación
                </p>
                <div className="space-y-1.5">
                  <ShortcutRow keys="↑ / k" desc="Cuenta anterior" />
                  <ShortcutRow keys="↓ / j" desc="Cuenta siguiente" />
                  <ShortcutRow keys="Enter" desc="Abrir cuenta seleccionada" />
                  <ShortcutRow keys="/" desc="Buscar cuentas" />
                  <ShortcutRow keys="Esc" desc="Salir de búsqueda" />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
